import React, { useState, useMemo } from 'react';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';
import { XMarkIcon } from '@heroicons/react/20/solid';
import logger from '../../utils/logger';

const DuplicateManager = ({ isOpen, onClose, items, collectionName, duplicateCriteria, itemLabel }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // Find duplicates based on the provided criteria
  const duplicateGroups = useMemo(() => {
    const groups = {};

    items.forEach(item => {
      // Create a key based on the duplicate criteria
      const key = duplicateCriteria.map(field => String(item[field] || '').toLowerCase().trim()).join('|');

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
    });

    // Filter out groups with only one item (not duplicates)
    const duplicates = Object.entries(groups)
      .filter(([_, group]) => group.length > 1)
      .map(([key, group]) => ({
        key,
        items: group.sort((a, b) => {
          // Sort by creation date if available
          const dateA = a.createdAt?.seconds || a.dateCreated?.seconds || 0;
          const dateB = b.createdAt?.seconds || b.dateCreated?.seconds || 0;
          return dateB - dateA; // Newest first
        })
      }));

    return duplicates;
  }, [items, duplicateCriteria]);

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleSelectAll = (group) => {
    const groupIds = group.items.slice(1).map(item => item.id); // Keep the first (newest), select others
    setSelectedItems(prev => {
      const newSet = new Set([...prev, ...groupIds]);
      return Array.from(newSet);
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) {
      toast.warning('Please select items to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} duplicate ${itemLabel}(s)? This action cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const itemId of selectedItems) {
      try {
        await deleteDoc(doc(db, collectionName, itemId));
        successCount++;
      } catch (error) {
        logger.error(`Error deleting ${itemLabel}:`, error);
        errorCount++;
      }
    }

    setIsDeleting(false);
    setSelectedItems([]);

    if (successCount > 0) {
      toast.success(`Successfully deleted ${successCount} duplicate ${itemLabel}(s)`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to delete ${errorCount} ${itemLabel}(s)`);
    }

    // Close modal after successful deletion
    if (successCount > 0 && errorCount === 0) {
      setTimeout(() => {
        onClose();
      }, 1500);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString('en-GB');
  };

  const getDisplayValue = (item, field) => {
    const value = item[field];
    if (!value) return 'N/A';
    if (typeof value === 'object' && value.seconds) {
      return formatDate(value);
    }
    return String(value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 flex justify-between items-center">
            <h3 className="text-xl font-semibold text-white">
              Duplicate {itemLabel} Manager
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <div className="bg-white px-6 py-4 max-h-[70vh] overflow-y-auto">
            {duplicateGroups.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">✓</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No Duplicates Found!
                </h3>
                <p className="text-gray-500">
                  All {itemLabel}s in your database are unique.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>Found {duplicateGroups.length} duplicate group(s)</strong> with a total of{' '}
                    {duplicateGroups.reduce((sum, g) => sum + g.items.length - 1, 0)} duplicate {itemLabel}(s).
                    <br />
                    <span className="text-xs">
                      The newest record in each group is highlighted in green. Select the duplicates you want to delete.
                    </span>
                  </p>
                </div>

                {duplicateGroups.map((group, groupIndex) => (
                  <div key={group.key} className="mb-6 border rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-4 py-2 flex justify-between items-center">
                      <h4 className="font-semibold text-gray-700">
                        Duplicate Group #{groupIndex + 1} ({group.items.length} items)
                      </h4>
                      <button
                        onClick={() => handleSelectAll(group)}
                        className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded"
                      >
                        Select All Duplicates
                      </button>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Select
                            </th>
                            {duplicateCriteria.map(field => (
                              <th key={field} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {field.replace(/([A-Z])/g, ' $1').trim()}
                              </th>
                            ))}
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Created Date
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {group.items.map((item, index) => {
                            const isNewest = index === 0;
                            const isSelected = selectedItems.includes(item.id);
                            return (
                              <tr
                                key={item.id}
                                className={`${isNewest ? 'bg-green-50' : ''} ${isSelected ? 'bg-red-50' : ''}`}
                              >
                                <td className="px-3 py-2">
                                  {!isNewest && (
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleSelectItem(item.id)}
                                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                                    />
                                  )}
                                  {isNewest && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded">
                                      Keep
                                    </span>
                                  )}
                                </td>
                                {duplicateCriteria.map(field => (
                                  <td key={field} className="px-3 py-2 text-sm text-gray-900">
                                    {getDisplayValue(item, field)}
                                  </td>
                                ))}
                                <td className="px-3 py-2 text-sm text-gray-500">
                                  {formatDate(item.createdAt || item.dateCreated || item.invoiceDate)}
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  {isNewest ? (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                      Newest
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full">
                                      Duplicate
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          {duplicateGroups.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
              <div className="text-sm text-gray-600">
                {selectedItems.length > 0 ? (
                  <span className="font-semibold text-red-600">
                    {selectedItems.length} item(s) selected for deletion
                  </span>
                ) : (
                  <span>No items selected</span>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteSelected}
                  disabled={selectedItems.length === 0 || isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : `Delete Selected (${selectedItems.length})`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DuplicateManager;
